import config from '@app/config';
import { concatAllPairs } from '@app/utils/stringUtils';
import { arrayWithoutValue } from '@app/utils/arrayUtils';
import pathToRegexp from 'path-to-regexp';

// An end that doesn't parse hides the label rather than showing it for good.
export function findShownSectionLabel(
  labelsByPath: Readonly<Record<string, SectionLabel>> | undefined,
  path: string,
  now: number,
): SectionLabel | undefined {
  const label = labelsByPath?.[path];
  return label && now < Date.parse(label.endsAtUtc) ? label : undefined;
}

export const SectionCategoryNames: Record<SectionCategory, string> = {
  incomingPayments: 'INCOMING PAYMENTS',
  outgoingPayments: 'OUTGOING PAYMENTS',
  validations: 'VALIDATIONS',
  management: 'MANAGEMENT',
};

/**
 * Resolves a pathname to a section's `name` for analytics page_title.
 * Walks all sections' flat routes, tests each `pattern` against the
 * pathname, and returns the matching section's `name`. Returns
 * `undefined` when no route matches — callers should fall back to
 * `document.title`.
 *
 * Mirrors the route-matching logic in NavigationStore's autorun, but
 * runs synchronously at call time so it doesn't depend on the autorun
 * having fired yet.
 */
export function getSectionTitle(sections: Section[], pathname: string): string | undefined {
  for (const section of sections) {
    for (const route of section.routes) {
      if (route.pattern?.test(pathname)) {
        return section.name;
      }
    }
  }
  return undefined;
}

export function fromDefinition(sectionDefinition: SectionDefinition, isModeAnonymous: boolean): Section {
  const routes = flatRoutes(sectionDefinition.routes).map((route) => fromRouteDefinition(route, sectionDefinition.id));

  return {
    ...sectionDefinition,
    routes,
    baseRoute: routes[0],
    isAnonymous: sectionDefinition.isAnonymous || isModeAnonymous,
  };
}

function fromRouteDefinition(routeDefinition: HierarchicalRoute, sectionId: SectionKey): FlatRoute {
  const { redirectFromPaths, path, ...restOfRouteDefinition } = routeDefinition;
  const pattern = pathToRegexp(path || '/');
  const pathBuilder = pathToRegexp.compile(path || '/');

  return {
    ...restOfRouteDefinition,
    sectionId,
    pattern,
    pathBuilder,
    redirectFromPaths: redirectFromPaths || [],
    path: config.urlPath + path,
  };
}

function flatRoutes(
  routes: HierarchicalRoute[] | undefined,
  baseRoute: string = '',
  baseRedirectedFrom: string[] = [],
): HierarchicalRoute[] {
  const formatterBaseRoute = baseRoute === '/' ? '' : baseRoute;

  const routesWithInner = (routes || []).map((route) => {
    const path = `${formatterBaseRoute}${route.path}`;
    const routeWithRealPath: HierarchicalRoute = {
      ...route,
      path,
      redirectFromPaths: arrayWithoutValue(
        concatAllPairs([baseRoute, ...baseRedirectedFrom], [route.path, ...(route.redirectFromPaths ?? [])]),
        path,
      ),
    };

    return [routeWithRealPath, ...flatRoutes(route.innerRoutes, routeWithRealPath.path, routeWithRealPath.redirectFromPaths)];
  });

  return routesWithInner.flat();
}
